import { createQuery, QueryableRow } from "@nova/queryable"
import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react"
import { Table } from "@nova/ds-component-table"
import { TriCheckbox } from "@nova/component-checkbox"
import { LinkText } from "components/LinkText"
import { usePilotTrackingQuery } from "features/pilot-tracking/PilotTrackingQueries"
import { ButtonDestructive, ButtonPrimary, IconButton } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import {
    selectSearch,
    selectView,
    setSearch,
    setView,
} from "features/pilot-tracking/aircraft/slice"
import { useConfirmationModalContext } from "features/modals/WorkspaceConfirmationModalV2"
import { AddAircraftModal } from "features/pilot-tracking/aircraft/AddAircraftModal"
import { DeleteAircraftModal } from "features/pilot-tracking/aircraft/DeleteAircraftModal"
import { DeleteMultipleAircraftModal } from "features/pilot-tracking/aircraft/DeleteMultipleAircraftModal"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectSelectedAircraftIds } from "features/pilot-tracking/redux/selectors"
import {
    clearSelectedAircraftIds,
    setAircraftSelected,
    toggleAircraftSelected,
} from "features/pilot-tracking/redux/slice"
import { selectActiveContextMenu } from "features/popups/contextMenu"
import { setActiveContextMenu } from "features/popups/slice"
import { ContextMenu } from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { useRouter } from "pages"
import { getGenericWorkspacePage } from "../WorkspaceRoutes"
import {
    AircraftComputedStatus,
    useAircraftStatuses,
} from "features/pilot-tracking/aircraft/AircraftStatusQueries"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import {
    getDisplayableAircraftData,
    getDroneMakeDisplayName,
    getDroneModelDisplayName,
    normalizeDroneMake,
    normalizeDroneModel,
} from "@nova/constants"
import { ViewSwitcher } from "features/data-management/workspace/workspace/body/navigation-bar/breadcrumb-bar/view-switcher/ViewSwitcher"
import { InputOutlined } from "@nova/ds-component-input"
import { AircraftStatusBadgeDisplay } from "features/pilot-tracking/aircraft/AircraftStatusBadge"
import { AircraftImage } from "@nova/component-aircraft-images"

export const AircraftsPage = getGenericWorkspacePage({
    Header: AircraftsPageHeader,
    Content: AircraftsPageContent,
    maxWidth: "unbound",
})

const aircraftsQuery = createQuery({
    resource: "aircraft",
    select: ["id", "nickname", "manufacturer", "model", "manual_status"],
    aggregates: [
        { field: "missions.id", func: "COUNT", alias: "numFlights" },
        { field: "missions.metadata.airtime_hours", func: "SUM", alias: "totalFlightTime" },
        { field: "missions.metadata.started_at", func: "MAX", alias: "lastFlight" },
    ],
})

function AircraftsPageHeader() {
    const dispatch = useAppDispatch()

    const selectedAircraftIds = useAppSelector(selectSelectedAircraftIds)
    const view = useAppSelector(selectView)
    const search = useAppSelector(selectSearch)

    const { data } = usePilotTrackingQuery(aircraftsQuery)
    const { isEditor } = useWorkspacePermissions()
    const modal = useConfirmationModalContext()

    const numSelected = Object.keys(selectedAircraftIds).length

    const handleUploadClick = async () => {
        await modal.confirm({
            RenderComponent: ({ cancel, confirm }) => (
                <AddAircraftModal
                    onCancel={cancel}
                    onSubmit={({ aircraftName, success }) => {
                        dispatch(
                            setGlobalNotificationContext({
                                message: success
                                    ? `${aircraftName} has been created.`
                                    : `Failed to create ${aircraftName}. Please try again.`,
                                status: success
                                    ? GlobalNotificationStatus.success
                                    : GlobalNotificationStatus.error,
                                timeoutInMs: 3000,
                            })
                        )
                        confirm()
                    }}
                />
            ),
        })
    }
    const handleDeleteClick = async (id: string, nickname: string) => {
        const confirmed = await modal.confirm({
            RenderComponent: ({ cancel, confirm }) => (
                <DeleteAircraftModal
                    aircraftId={id}
                    nickname={nickname}
                    onCancel={cancel}
                    onSubmit={confirm}
                />
            ),
        })
        if (confirmed) {
            dispatch(clearSelectedAircraftIds())
        }
    }
    const handleBulkDeleteClick = async () => {
        if (!data) return
        const selected = data.filter((d) => selectedAircraftIds[d.id])
        if (selected.length === 1) {
            const s = selected[0]
            const { displayName } = getDisplayableAircraftData(s)
            await handleDeleteClick(s.id, displayName)
        } else {
            const confirmed = await modal.confirm({
                RenderComponent: ({ cancel, confirm }) => (
                    <DeleteMultipleAircraftModal
                        aircraft={selected}
                        onCancel={cancel}
                        onSubmit={confirm}
                    />
                ),
            })
            if (confirmed) {
                dispatch(clearSelectedAircraftIds())
            }
        }
    }
    return (
        <div className="flex items-center justify-between h-full">
            <div className="flex items-center gap-2.5">
                <div className="text-[1.375rem] telegraf flex items-center">All Drones</div>
                {isEditor && !!numSelected && (
                    <>
                        <IconButton onClick={() => dispatch(clearSelectedAircraftIds())}>
                            <Icons.SmallIcon
                                Icon={Icons.XClose}
                                iconClassName="text-gray-300 hover:text-white transition-colors"
                            />
                        </IconButton>
                        <h5 className="text-white">{numSelected} selected</h5>
                        <ButtonDestructive
                            title="Delete"
                            LeftIcon={Icons.Trash}
                            onClick={handleBulkDeleteClick}
                        />
                    </>
                )}
            </div>
            <div className="flex items-center gap-2.5">
                <ViewSwitcher
                    firstOption={{
                        icon: Icons.Grid,
                        isSelected: view === "grid",
                        onSwitch: () => dispatch(setView("grid")),
                    }}
                    secondOption={{
                        icon: Icons.List,
                        isSelected: view === "table",
                        onSwitch: () => dispatch(setView("table")),
                    }}
                />
                <div className="min-w-[228px] px-px">
                    <InputOutlined
                        placeholder="Search"
                        leftItem={<Icons.Icon Icon={Icons.MagnifyingGlass} />}
                        value={search}
                        onChange={(val) => dispatch(setSearch(val.currentTarget.value))}
                        size="sm"
                    />
                </div>
                {isEditor && (
                    <ButtonPrimary
                        LeftIcon={Icons.Plus}
                        title="Drone"
                        onClick={handleUploadClick}
                    />
                )}
            </div>
        </div>
    )
}

function AircraftsPageContent() {
    const dispatch = useAppDispatch()

    const search = useAppSelector(selectSearch)
    const view = useAppSelector(selectView)
    const contextMenu = useAppSelector(selectActiveContextMenu("Aircraft"))

    const { data, isLoading } = usePilotTrackingQuery(aircraftsQuery)
    const statuses = useAircraftStatuses(data)
    const modal = useConfirmationModalContext()
    const { isEditor } = useWorkspacePermissions()

    const router = useRouter("/workspace/:workspaceId")

    const filteredData = useMemo(() => {
        if (!data) return data
        const q = search.toLowerCase()
        if (!q) return data
        return data.filter(
            (d) =>
                d.nickname?.toLowerCase().includes(q) ||
                d.manufacturer?.toLowerCase().includes(q) ||
                d.model?.toLowerCase().includes(q)
        )
    }, [data, search])

    const handleDeleteClick = async (id: string, nickname: string) => {
        const confirmed = await modal.confirm({
            RenderComponent: ({ cancel, confirm }) => (
                <DeleteAircraftModal
                    aircraftId={id}
                    nickname={nickname}
                    onCancel={cancel}
                    onSubmit={confirm}
                />
            ),
        })
        if (confirmed) {
            dispatch(clearSelectedAircraftIds())
        }
    }

    if (isLoading) {
        return (
            <div className="w-full h-full flex justify-center items-center">
                <div className="loader" />
            </div>
        )
    }

    if (!data?.length) {
        return (
            <div className="flex flex-col items-center gap-2">
                <h1 className="text-white">No Drones Found</h1>
                <h5 className="text-gray-100">
                    Drones will automatically appear when flights are uploaded, or you can create
                    one manually.
                </h5>
            </div>
        )
    }

    return (
        <>
            {view === "grid" ? (
                <div className="w-full flex justify-center">
                    <div className="max-w-[1200px] w-full grid gap-3 grid-cols-[repeat(auto-fill,minmax(240px,1fr))]">
                        {filteredData?.map((d) => (
                            <div
                                key={d.id}
                                className="cursor-pointer h-fit"
                                onClick={() =>
                                    router.navigate({
                                        path: "/fleet/aircraft/:aircraftId",
                                        params: { aircraftId: d.id },
                                    })
                                }
                                onContextMenu={
                                    isEditor
                                        ? (e) => {
                                              e.preventDefault()
                                              const make = normalizeDroneMake(d.manufacturer ?? "")
                                              const modelKey =
                                                  make && d.model
                                                      ? normalizeDroneModel(make, d.model)
                                                      : undefined
                                              const modelDisplay = modelKey
                                                  ? getDroneModelDisplayName(make!, modelKey)
                                                  : d.model
                                              dispatch(
                                                  setActiveContextMenu({
                                                      type: "Aircraft",
                                                      data: {
                                                          id: d.id,
                                                          nickname:
                                                              d.nickname ??
                                                              modelDisplay ??
                                                              (make
                                                                  ? getDroneMakeDisplayName(make)
                                                                  : d.manufacturer) ??
                                                              "",
                                                      },
                                                      position: { top: e.clientY, left: e.clientX },
                                                  })
                                              )
                                          }
                                        : undefined
                                }
                            >
                                <AircraftCard
                                    aircraft={{
                                        id: d.id,
                                        status: statuses?.[d.id] ?? null,
                                        nickname: d.nickname,
                                        manufacturer: d.manufacturer,
                                        model: d.model,
                                        numFlights: +d.numFlights,
                                        totalFlightTime:
                                            d.totalFlightTime != null ? +d.totalFlightTime : null,
                                        lastFlight: d.lastFlight,
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="pb-12 h-full">
                    <AircraftTable data={filteredData ?? []} statuses={statuses} />
                </div>
            )}
            {isEditor && contextMenu && (
                <ContextMenu
                    sections={[
                        [
                            {
                                title: "Delete",
                                Icon: Icons.Trash,
                                isDestructive: true,
                                onClick: () => {
                                    const { id, nickname } = contextMenu.data
                                    dispatch(setActiveContextMenu(undefined))
                                    handleDeleteClick(id, nickname)
                                },
                            },
                        ],
                    ]}
                    onBlur={() => dispatch(setActiveContextMenu(undefined))}
                    isFixedPosition={true}
                    top={contextMenu.position.top}
                    left={contextMenu.position.left}
                />
            )}
        </>
    )
}

interface AircraftCardProps {
    aircraft: {
        id: string
        status: AircraftComputedStatus | null
        nickname: string | null
        manufacturer: string | null
        model: string | null
        numFlights: number
        totalFlightTime: number | null
        lastFlight: Date | null
    }
}

const AircraftCard: FunctionComponent<AircraftCardProps> = ({ aircraft }) => {
    const { displayName, model } = getDisplayableAircraftData(aircraft)

    const flightTimeHours =
        aircraft.totalFlightTime != null ? `${Math.round(aircraft.totalFlightTime)} hours` : "—"

    const lastFlightStr = aircraft.lastFlight
        ? new Date(aircraft.lastFlight).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
          })
        : "—"

    return (
        <div className="flex flex-col p-2 gap-4 bg-gray-900 group/aircraftCard rounded-xl border border-white-100 ring-inset hover:ring-2 hover:ring-white-400 transition-all">
            <div className="flex justify-between items-center">
                <AircraftStatusBadgeDisplay status={aircraft.status} />
                <small className="text-xs text-gray-300">Flights - {aircraft.numFlights}</small>
            </div>
            <div className="flex items-center justify-center px-6 py-4">
                <AircraftImage quality="half" make={aircraft.manufacturer} model={aircraft.model} />
            </div>
            <div className="flex flex-col gap-4 px-2 pb-4">
                <div className="flex flex-col gap-0.5">
                    <h1 className="text-white truncate">{displayName}</h1>
                    <small className="text-gray-300">{model}</small>
                </div>
                <div className="flex gap-6">
                    <div className="flex flex-col gap-0.5">
                        <small className="text-gray-500">Last Flight</small>
                        <h5 className="text-gray-100">{lastFlightStr}</h5>
                    </div>
                    <div className="flex flex-col gap-0.5">
                        <small className="text-gray-500">Flight Time</small>
                        <h5 className="text-gray-100">{flightTimeHours}</h5>
                    </div>
                </div>
            </div>
        </div>
    )
}

type AircraftRow = QueryableRow<typeof aircraftsQuery>

type SortDir = "DESC" | "ASC"
type SortField = "nickname" | "model" | "lastFlight" | "numFlights" | "totalFlightTime"

interface SortState {
    field: SortField
    dir: SortDir
}

interface AircraftTableProps {
    data: AircraftRow[]
    statuses: ReturnType<typeof useAircraftStatuses>
}

const AircraftTable = ({ data, statuses }: AircraftTableProps) => {
    const dispatch = useAppDispatch()
    const router = useRouter("/workspace/:workspaceId")
    const selectedAircraftIds = useAppSelector(selectSelectedAircraftIds)
    const numSelected = Object.keys(selectedAircraftIds).length
    const { isEditor } = useWorkspacePermissions()
    const [sort, setSort] = useState<SortState | undefined>()
    const anchorId = useRef<string | null>(null)

    useEffect(() => {
        return () => {
            dispatch(clearSelectedAircraftIds())
        }
    }, [dispatch])

    const sortedData = useMemo(() => {
        if (!sort) return data
        const { field, dir } = sort
        const mul = dir === "ASC" ? 1 : -1
        return data.toSorted((a, b) => {
            const av = a[field]
            const bv = b[field]
            if (av == null && bv == null) return 0
            if (av == null) return 1
            if (bv == null) return -1
            if (av < bv) return -mul
            if (av > bv) return mul
            return 0
        })
    }, [data, sort])

    const handleSort = (field: SortField) => {
        setSort((prev) => {
            if (prev?.field !== field) return { field, dir: "DESC" }
            if (prev.dir === "DESC") return { field, dir: "ASC" }
            return undefined
        })
    }

    const aircraftIds = sortedData.map((d) => d.id)

    return (
        <Table className="h-full">
            <Table.Head>
                <Table.Row>
                    {isEditor && (
                        <Table.Header>
                            <TriCheckbox
                                state={
                                    numSelected === aircraftIds.length && aircraftIds.length > 0
                                        ? "checked"
                                        : numSelected > 0
                                          ? "indeterminate"
                                          : "unchecked"
                                }
                                onClick={() => {
                                    if (numSelected === aircraftIds.length) {
                                        dispatch(clearSelectedAircraftIds())
                                    } else {
                                        aircraftIds.forEach((id) =>
                                            dispatch(setAircraftSelected({ id, selected: true }))
                                        )
                                    }
                                }}
                                size="small"
                            />
                        </Table.Header>
                    )}
                    <Table.Header
                        className="cursor-pointer select-none"
                        onClick={() => handleSort("nickname")}
                    >
                        <div className="flex items-center gap-1">
                            <div>Name</div>
                            <Table.SortIndicator
                                sortState={sort?.field === "nickname" ? sort.dir : "none"}
                            />
                        </div>
                    </Table.Header>
                    <Table.Header
                        className="cursor-pointer select-none"
                        onClick={() => handleSort("model")}
                    >
                        <div className="flex items-center gap-1">
                            <div>Model</div>
                            <Table.SortIndicator
                                sortState={sort?.field === "model" ? sort.dir : "none"}
                            />
                        </div>
                    </Table.Header>
                    <Table.Header>
                        <div>Condition</div>
                    </Table.Header>
                    <Table.Header
                        className="cursor-pointer select-none"
                        onClick={() => handleSort("lastFlight")}
                    >
                        <div className="flex items-center gap-1">
                            <div>Last Flight</div>
                            <Table.SortIndicator
                                sortState={sort?.field === "lastFlight" ? sort.dir : "none"}
                            />
                        </div>
                    </Table.Header>
                    <Table.Header
                        className="cursor-pointer select-none"
                        onClick={() => handleSort("numFlights")}
                    >
                        <div className="flex items-center gap-1">
                            <div>Total Flights</div>
                            <Table.SortIndicator
                                sortState={sort?.field === "numFlights" ? sort.dir : "none"}
                            />
                        </div>
                    </Table.Header>
                    <Table.Header
                        className="cursor-pointer select-none"
                        onClick={() => handleSort("totalFlightTime")}
                    >
                        <div className="flex items-center gap-1">
                            <div>Total Flight Time</div>
                            <Table.SortIndicator
                                sortState={sort?.field === "totalFlightTime" ? sort.dir : "none"}
                            />
                        </div>
                    </Table.Header>
                </Table.Row>
            </Table.Head>
            <Table.Body className="relative">
                {sortedData.map((d) => {
                    const { displayName, model } = getDisplayableAircraftData(d)
                    return (
                        <Table.Row key={d.id}>
                            {isEditor && (
                                <Table.Cell>
                                    <TriCheckbox
                                        state={
                                            !!selectedAircraftIds[d.id] ? "checked" : "unchecked"
                                        }
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            const a = aircraftIds.indexOf(anchorId.current!)
                                            const b = aircraftIds.indexOf(d.id)
                                            anchorId.current = d.id
                                            if (!e.shiftKey || a === -1)
                                                return dispatch(toggleAircraftSelected(d.id))
                                            aircraftIds
                                                .slice(Math.min(a, b), Math.max(a, b) + 1)
                                                .forEach((id) =>
                                                    dispatch(
                                                        setAircraftSelected({ id, selected: true })
                                                    )
                                                )
                                        }}
                                        size="small"
                                    />
                                </Table.Cell>
                            )}
                            <Table.Cell>
                                <router.Link
                                    to={{
                                        path: "/fleet/aircraft/:aircraftId",
                                        params: { aircraftId: d.id },
                                    }}
                                >
                                    <LinkText>{displayName}</LinkText>
                                </router.Link>
                            </Table.Cell>
                            <Table.Cell>{model}</Table.Cell>
                            <Table.Cell>
                                <AircraftStatusBadgeDisplay status={statuses?.[d.id] ?? null} />
                            </Table.Cell>
                            <Table.Cell>
                                {d.lastFlight
                                    ? new Date(d.lastFlight).toLocaleDateString("en-US", {
                                          month: "short",
                                          day: "numeric",
                                          year: "numeric",
                                      })
                                    : "—"}
                            </Table.Cell>
                            <Table.Cell>{+d.numFlights}</Table.Cell>
                            <Table.Cell>
                                {d.totalFlightTime != null
                                    ? `${Math.round(+d.totalFlightTime)} hours`
                                    : "—"}
                            </Table.Cell>
                        </Table.Row>
                    )
                })}
            </Table.Body>
        </Table>
    )
}
